/**
 * "Set a new password" dialog behind the Oracle expired-password fix chip
 * (ORA-28001 / ORA-28002) on the connection-error notice.
 *
 * Submitting performs Oracle's password-change-at-connect over IPC using the
 * failing connection's current credentials. Only after the server accepted
 * the change does `onPasswordChanged` hand the new password back to the
 * caller, which retries the connect with it. Unlike the speculative TLS
 * chips, by that point the server-side password HAS changed, so callers keep
 * (and the saved-list surface persists) the new value even when the retry
 * fails — reverting would restore a password that can never connect again.
 */
import { useEffect, useRef, useState } from 'react';
import type { Connection } from '../../../types/electron';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { safeConnectionName } from '@/types/safeConnectionDisplay';

type OracleExpiredPasswordDialogProps = {
  /** The failing connection — carries the credentials and connect target. */
  connection: Connection;
  /** Called after the server accepted the change; the caller retries with it. */
  onPasswordChanged: (newPassword: string) => void;
  onClose: () => void;
};

export function OracleExpiredPasswordDialog({
  connection,
  onPasswordChanged,
  onClose,
}: OracleExpiredPasswordDialogProps) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The IPC continuation outlives the component when a parent tears the
  // whole surface down mid-change (the dialog blocks its own close while
  // busy, but not the popup/list hosting it). A late `onPasswordChanged`
  // would start a retry — and on the list surface a persist — after the UI
  // that asked for it is gone, so the continuation checks this first.
  const mountedRef = useRef(true);
  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const subtitle = `${connection.login || 'user'} @ ${safeConnectionName(connection)}`;

  const handleSubmit = async () => {
    if (busy) return;
    if (!newPassword) {
      setError('Enter a new password.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('The passwords do not match.');
      return;
    }

    const changeExpiredPassword = window.electronAPI?.oracle?.changeExpiredPassword;
    if (!changeExpiredPassword) {
      setError('Password change is unavailable in this build.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const result = await changeExpiredPassword({ connection, newPassword });
      if (!mountedRef.current) return;
      if (result.success) {
        onPasswordChanged(newPassword);
      } else {
        // Oracle's own message (e.g. a password-verify-function rejection)
        // is the actionable part — show it where the user can act on it.
        setError(result.error || 'Oracle rejected the password change.');
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : 'Unknown error changing the password.');
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="oracle-expired-password-dialog">
        <DialogHeader>
          <DialogTitle>Set a new Oracle password</DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground -mt-1 break-all">{subtitle}</p>
        <p className="text-xs text-muted-foreground -mt-1">
          The database reported this account&apos;s password as expired. Oracle allows setting a
          new one while connecting — the connection retries with it right away.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
          className="space-y-3"
        >
          <Input
            type="password"
            autoFocus
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="New password"
            data-testid="oracle-new-password-input"
            aria-label="New password"
          />
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            data-testid="oracle-confirm-password-input"
            aria-label="Confirm new password"
          />

          {error && (
            <p
              className="text-xs text-destructive break-words"
              data-testid="oracle-expired-password-error"
            >
              {error}
            </p>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy} data-testid="oracle-expired-password-submit">
              {busy ? <RandomDotmSpinner size={16} /> : null}
              Change password and retry
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
